import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot } from '@angular/router';
import { makeStateKey, TransferState } from '@angular/core';
import { get, toUpper, trim } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
// Service
import { PartSearchService } from './part-search.service';

// KEY
const STATE_KEY_PRODUCTS = makeStateKey('product');

export const ProductResolverService: ResolveFn<any> = (route: ActivatedRouteSnapshot): Observable<any> => {
    const partSearchService = inject(PartSearchService);
    const transferState = inject(TransferState);

    // Check if data is already provided by SSR
    if (transferState.hasKey(STATE_KEY_PRODUCTS)) {
        const result = of(transferState.get<any>(STATE_KEY_PRODUCTS, []));
        transferState.remove(STATE_KEY_PRODUCTS);
        return result;
    }

    const payload = getProductPayload(route);

    return partSearchService.getProducts(payload).pipe(
        take(1),
        catchError(() => of([]))
    );
};

/**
 * Generate product payload based on route data.
 * @param {ActivatedRouteSnapshot} route
 * @returns {any}
 */
const getProductPayload = (route: ActivatedRouteSnapshot): any => {
    const sourcePath: string = get(route, 'data.path', 'S1');

    switch (sourcePath) {
        case 'S1': // Regular Path
            return {
                sm: 1,
                code_vc_make_model: get(route, 'params.code_vc_make_model'),
                code_vc_myft: get(route, 'params.code_vc_myft'),
                pc1: get(route, 'params.pc1'),
                vt1: get(route, 'params.vt1'),
                path: '/{lang}/pb/{label_search}/{label_car_parts}/s1/{vc1}/{vc}/{vcx}/{pc1}',
            };

        case 'OV': // OV Path
            return {
                sm: 1,
                code_vc_make_model: get(route, 'params.code_vc_make_model'),
                code_vc_myft: get(route, 'params.code_vc_myft'),
                pc1: get(route, 'params.pc1'),
                vt1: get(route, 'params.vt1'),
                path: '/{lang}/ov/{label_search}/{label_car_parts}/{vc1}/{vc}/{vcx}/{pc1}',
            };

        case 'S6': // Engine Code
            const engineQuery = toUpper(trim(get(route, 'queryParams.query', '')));
            return {
                sm: 6,
                pc1: get(route, 'params.pc1'),
                query: engineQuery,
                path: `/{lang}/pb/{label_search}/{label_car_parts}/s6/{pc1}?query=${encodeURIComponent(engineQuery)}`
            };

        case 'S7': // Gearbox Code
            const gearboxQuery = toUpper(trim(get(route, 'queryParams.query', '')));
            return {
                sm: 7,
                pc1: get(route, 'params.pc1'),
                query: gearboxQuery,
                path: `/{lang}/pb/{label_search}/{label_car_parts}/s7/{pc1}?query=${encodeURIComponent(gearboxQuery)}`
            };

        case 'S9': // S9/AP (All Products)
            return {
                sm: 9,
                pc1: get(route, 'params.pc1'),
                path: '/{lang}/pb/{label_search}/{label_car_parts}/s9/AP/{pc1}'
            };

        case 'S17': // a/a Path
            return {
                sm: 17,
                pc1: get(route, 'params.pc1'),
                vt1: get(route, 'params.vt1'),
                path: '/{lang}/pb/{label_search}/{label_car_parts}/s17/{vc1}/a/a/{pc1}',
            };

        case 'S19': // S19 Path
            return {
                sm: 19,
                vt1: get(route, 'params.vt1'),
                vt2: get(route, 'params.vt2'),
                pc1: get(route, 'params.pc1'),
                path: '/{lang}/pb/{label_search}/{label_car_parts}/s19/{vc1}/{vc2}/{pc1}',
            };

        default:
            return {};
    }
};
